import { Fragment } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  
  ArrowDown,
  Crown,
  Church,
  Shield,
  Sailboat,
  Ship,
  Utensils,
  Footprints,
  Route as RouteIcon,
  Palette,
  Clock,
  Bike,
  MapPin,
  ShoppingBasket,
  ShoppingBag,
  Telescope,
  Coffee,
  Croissant,
  Waves,
  Home,
  Music,

  Ticket,
} from "lucide-react";
import { Mark } from "@/components/Mark";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";
import { GygTourBand, GygTivoliCard, TIVOLI_TICKET_URL } from "@/components/GetYourGuide";
import { spotImg, ITINERARY_3D } from "@/data/planner";

const SITE = "https://cphhighlights.com";

// Loads this itinerary's stops into the planner, day by day in the itinerary's
// own walking order (?trip= format with ";"-separated days). The planner keeps
// this exact arrangement — it never re-clusters a link that already decided the
// days. The day groups live in planner.js as ITINERARY_3D (shared with the
// planner's auto-fill button).
const ITINERARY_TRIP_URL =
  "/planner?trip=any~3~" + ITINERARY_3D.map((g) => g.join(",")).join(";");

// Each day as a ready-made Google Maps walking route — the day's stops in the
// itinerary's own order (concrete picks for the "take your pick" stops), so
// readers can open the whole day as directions and save it to their phone.
const cph = (s) => encodeURIComponent(`${s}, Copenhagen`);
const mapsRoute = (stops) =>
  `https://www.google.com/maps/dir/?api=1&origin=${cph(stops[0])}&destination=${cph(stops[stops.length - 1])}` +
  (stops.length > 2 ? `&waypoints=${stops.slice(1, -1).map(cph).join("%7C")}` : "") +
  "&travelmode=walking";

const DAY_ROUTES = {
  // Day 1 meal/activity pins are the user's exact Google Maps places:
  // hotdog = Pølsevognen Kongens Nytorv, cruise = Stromma departure Nyhavn 3,
  // dinner = La Vecchia Signora, Grønnegade.
  1: ["Mormors, Bredgade 45", "Kastellet", "Nyboder", "Amalienborg", "Marmorkirken", "Pølsevognen, Kongens Nytorv 4", "Nyhavn 3", "Broens Gadekøkken", "La Vecchia Signora, Grønnegade 12"],
  2: ["Torvehallerne", "Rundetårn", "Gråbrødretorv", "Skt. Peders Bageri, Sankt Peders Stræde 29", "Floss Bar, Larsbjørnsstræde 10", "Strøget", "Tivoli Gardens"],
  // Day 3 dinner pin is the user's exact place: Christianshavns Færgecafé, Strandgade 50.
  3: ["The Packhouse, Overgaden Neden Vandet 19A", "Vor Frelsers Kirke", "Freetown Christiania", "Reffen", "La Banchina, Refshalevej 141", "Opera Park", "Christianshavns Færgecafé, Strandgade 50"],
};

const DayRoute = ({ day }) => (
  <a
    href={mapsRoute(DAY_ROUTES[day])}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`day-${day}-route`}
    className="inline-flex items-center gap-2 font-display font-900 text-base bg-black text-white px-6 py-3.5 rounded-full hover:-translate-y-0.5 hover:shadow-xl transition-all"
  >
    <RouteIcon size={16} /> Day {day} as a walking route
  </a>
);

// Unsplash photos sized for the web (WebP/AVIF via auto=format).
const sz = (url, w = 1000) => `${url}&w=${w}&q=75&auto=format&fit=crop`;

const IMG = {
  hero: spotImg("nyhavn", 1600),
  amalienborg: spotImg("amalienborg", 1000),
  marmorkirken: sz(
    "https://images.unsplash.com/photo-1781455202668-684572cff620?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxDb3BlbmhhZ2VuJTIwTWFyYmxlJTIwQ2h1cmNoJTIwTWFybW9ya2lya2VuJTIwZG9tZXxlbnwwfHx8fDE3ODcyNTM4MTJ8MA&ixlib=rb-4.1.0&q=85"
  ),
  kastellet: sz(
    "https://images.unsplash.com/photo-1692388646650-5aedc9a8ed2f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxDb3BlbmhhZ2VuJTIwS2FzdGVsbGV0JTIwZm9ydHJlc3MlMjB3aW5kbWlsbHxlbnwwfHx8fDE3ODcyNTM4MTJ8MA&ixlib=rb-4.1.0&q=85"
  ),
  hotdog: "/planner/hotdog-cph.webp",
  nyhavn: spotImg("nyhavn", 1000),
  broens: spotImg("broens", 1000),
  canalstroll: spotImg("canalstroll", 1000),
  oursaviour: spotImg("oursaviour", 1000),
  smorrebrod: spotImg("smorrebrod", 1000),
  christiania: spotImg("christiania", 1000),
  tivoli: spotImg("tivoli", 1000),
  gasolinegrill: spotImg("gasolinegrill", 1000),
  torvehallerne: spotImg("torvehallerne", 1000),
  rundetaarn: spotImg("rundetaarn", 1000),
  graabroedretorv: spotImg("graabroedretorv", 1000),
  latinquarter: spotImg("latinquarter", 1000),
  stroget: spotImg("stroget", 1000),
  reffen: spotImg("reffen", 1000),
  mormor: sz(
    "https://images.unsplash.com/photo-1593999441887-ddabb2195944?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxjb3p5JTIwcXVpcmt5JTIwdmludGFnZSUyMGNhZiVDMyVBOSUyMGJyZWFrZmFzdCUyMHBhc3RyeSUyMGNvZmZlZXxlbnwwfHx8fDE3ODgyODEyMjd8MA&ixlib=rb-4.1.0&q=85"
  ),
  nyboder: spotImg("nyboder", 1000),
  parterre: "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/vqlv1nqi_20260805_170613.webp",
  opera:
    "https://thumb.wikimedia.org/wikipedia/commons/thumb/9/94/Kopenhagen_%28DK%29%2C_Hafen_und_Opernhaus_--_2017_--_1713.jpg/1280px-Kopenhagen_%28DK%29%2C_Hafen_und_Opernhaus_--_2017_--_1713.jpg",
  dinnerD1:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/mta5y2cy_20240617_191105.webp",
  dinnerD2:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/s309n34z_2026-09-01%2010_38_45.659%2B0200.webp",
  dinnerD3:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_explore-copenhagen/artifacts/1lupok8s_Screenshot_20260902_152321_Instagram.webp",
};

const mdBold = (text) =>
  typeof text !== "string"
    ? text
    : text.split(/\[\[(.+?)\|(\/[^|\]]+?)\]\]/g).map((part, i, arr) => {
        if (i % 3 === 2) return null; // url part — consumed by the link before it
        if (i % 3 === 1)
          return (
            <Link
              key={i}
              to={arr[i + 1]}
              className="font-800 text-[#DF3226] underline decoration-[#DF3226]/30 underline-offset-2 hover:decoration-[#DF3226]"
            >
              {part}
            </Link>
          );
        return part
          .split(/\*\*(.+?)\*\*/g)
          .map((p, j) => (j % 2 === 1 ? <strong key={`${i}-${j}`} className="font-800 text-black">{p}</strong> : p));
      });

const DAY1 = [
  {
    time: "09:00",
    icon: Coffee,
    name: "Breakfast — take your pick",
    body:
      "Ease into the day with breakfast in the royal quarter — three lovely options, all a few minutes apart. **Mormors** (Bredgade 45) is a wonderfully quirky, old-fashioned café that feels like your grandmother's living room (mormor means grandmother in Danish). **Hart** (Store Kongensgade 75) is one of the city's very best bakeries — outstanding pastries and coffee. Or **Original Coffee** (Bredgade 36) for a great, no-fuss cup to start the day.",
    img: IMG.mormor,
    imgAlt: "Cosy café breakfast with coffee and a croissant",
  },
  {
    time: "09:45",
    icon: Shield,
    name: "Kastellet",
    body:
      "Walk breakfast off with a morning wander to Kastellet, one of the best-preserved star fortresses in Northern Europe — grassy rampart, red barracks and a photo-worthy red windmill. Optional: the **Little Mermaid** sits just beyond the ramparts, so pop over if you fancy the classic photo — she's far smaller than everyone expects. Then loop back south in good time for the guards.",
    img: IMG.kastellet,
    imgAlt: "The historic windmill inside Kastellet fortress",
  },
  {
    time: "11:15",
    icon: Home,
    name: "Nyboder",
    body:
      "A short walk away, duck into Nyboder — rows of long, ochre-yellow terraced houses built by King Christian IV in the 1630s to house the navy, and still lived in today. It's a wonderfully quiet, storybook pocket of the city and a lovely photo stop. While you're here, wander over to nearby **Krusemyntegade** — a little street of colourful houses and climbing greenery that's made for photos.",
    img: IMG.nyboder,
    imgAlt: "Historic ochre-yellow terraced houses on a cobbled street",
  },
  {
    time: "11:45",
    icon: Crown,
    name: "Amalienborg — the Changing of the Guard",
    body:
      "Start royal. Arrive by 11:45 to claim a spot on the octagonal square before Den Kongelige Livgarde — the Royal Guards in bearskin hats — march in for the changing of the guard at 12:00 sharp.",
    img: IMG.amalienborg,
    imgAlt: "Amalienborg Palace square in Copenhagen",
  },
  {
    time: "12:30",
    icon: Church,
    name: "Marmorkirken (The Marble Church)",
    body:
      "Right by the palace, the Marble Church rises with the largest church dome in Scandinavia. Step inside (free) and tip your head all the way back to see the beautiful ceiling.",
    img: IMG.marmorkirken,
    imgAlt: "The green dome of Frederik's Church (Marmorkirken)",
  },
  {
    time: "14:00",
    icon: Utensils,
    name: "Lunch: a proper Danish hotdog",
    body:
      "Make your way to **Kongens Nytorv**, the grand old square that opens onto Nyhavn — there's a classic pølsevogn (hotdog cart) right there. Order a \"hotdog med det hele\" — a fried brown or a boiled red sausage in a bun with ketchup, mustard, remoulade, and both raw and crispy fried onions. It's the true Danish street-food institution, and cheap by Copenhagen standards.",
    img: IMG.hotdog,
    imgAlt: "A classic Danish hotdog with mustard and ketchup",
  },
  {
    time: "14:15",
    icon: Sailboat,
    name: "Nyhavn",
    body:
      "The picture you came to Copenhagen for. This 17th-century canal is lined with candy-coloured townhouses, old wooden ships and buzzing café terraces. [[Hans Christian Andersen|/hc-andersen]] lived here (at Nos. 20, 18 and 67). Grab a canal-side beer or just wander.",
    img: IMG.nyhavn,
    imgAlt: "The colourful houses of Nyhavn harbour",
  },
  {
    time: "16:30",
    icon: Utensils,
    name: "A harbour drink at Broens Gadekøkken",
    body:
      "Straight off the boat, cross the bridge (Inderhavnsbroen) to **Broens Gadekøkken**, the harbourside street-food hub. No dinner yet — just grab a cold beer or a glass of wine, find a spot by the water and let the day slow down for 45 minutes to an hour as the harbour glows.",
    img: IMG.broens,
    imgAlt: "Harbourside drinks at Broens Gadekøkken in Copenhagen",
  },
  {
    time: "18:00",
    icon: Utensils,
    name: "Dinner — take your pick",
    body:
      "Wander back towards Nyhavn for dinner. Take your pick: **Bistro Summér** (Nyhavn 65) for fine dining, **Nyhavnskroen** (Nyhavn 47) for traditional Danish dishes, or **La Vecchia Signora** (Grønnegade 12–14) for a real bite of Italy.",
    img: IMG.dinnerD1,
    imgAlt: "A plate of fresh pasta served for dinner in Copenhagen",
  },
];

const DAY3 = [
  {
    time: "09:30",
    icon: Coffee,
    name: "Breakfast in Christianshavn — take your pick",
    body:
      "Start day three with breakfast in Christianshavn — take your pick: **The Packhouse** (Overgaden Neden Vandet 19A), **Parterre** (Overgaden Oven Vandet 90), or a bowl of porridge at **Grød** (Prinsessegade 23).",
    img: IMG.parterre,
    imgAlt: "Parterre café terrace in Christianshavn, Copenhagen",
  },
  {
    time: "10:15",
    icon: Footprints,
    name: "A slow canal stroll in Christianshavn",
    body:
      "Walk it off along Christianshavn's canal — houseboats, leaning old warehouses and locals cycling by with coffee in hand. It's the most quietly photogenic corner of the city.",
    img: IMG.canalstroll,
    imgAlt: "A quiet canal in Christianshavn, Copenhagen",
  },
  {
    time: "11:00",
    icon: Church,
    name: "Climb Vor Frelsers Kirke (Church of Our Saviour)",
    body:
      "That black-and-gold corkscrew spire you keep spotting? Climb it. The 400 steps end with 150 winding up the outside of the spire itself, opening onto the most exhilarating 360° view in Copenhagen — the whole city, the harbour, and Sweden on a clear day. Not for the vertigo-prone; unforgettable for everyone else. Book in advance — it's popular. Note: the spire is closed in January and in bad weather.",
    extLink: {
      href: "https://www.vorfrelserskirke.dk/kontakt/taarnet/",
      label: "Book the climb in advance",
    },
    img: IMG.oursaviour,
    imgAlt: "The spiral golden spire of the Church of Our Saviour",
  },
  {
    time: "12:00",
    icon: Palette,
    name: "Christiania",
    body:
      "Straight from the spire, wander into Freetown Christiania — the car-free, self-governing community founded in 1971 on old military land. Expect murals, workshops, galleries and ramshackle self-built homes — a genuinely different rhythm inside the city.",
    img: IMG.christiania,
    imgAlt: "Colourful murals and houses in Freetown Christiania",
  },
  {
    time: "14:00",
    icon: Utensils,
    name: "Look around Reffen",
    body:
      "Head out to Refshaleøen for lunch at Reffen — Copenhagen's huge open-air street-food market, all shipping containers, harbour views and just about every cuisine going. Give it a good two hours; with food and the waterfront to soak up, it's an easy place to linger. Not feeling the street food? **Lille Bakery** (Refshalevej 213B) is the loveliest alternative — a tiny organic bakery and café in an old workshop building. Either way, finish with a scoop from **Ismageriet** on Reffen.",
    img: IMG.reffen,
    imgAlt: "Reffen street food market on Refshaleøen, Copenhagen",
  },
  {
    time: "16:00",
    icon: Waves,
    name: "Look around La Banchina",
    body:
      "A short stroll on, have a quick look at **La Banchina** (Refshalevej 141) — a tiny, rustic waterside spot with a wooden jetty right on the harbour. Prefer architecture or art? Swap it for **Urban Rigger** (Refshalevej 155) or **Copenhagen Contemporary** (Refshalevej 173A).",
    img: IMG.broens,
    imgAlt: "A rustic waterside wine bar on the Copenhagen harbour",
  },
  {
    time: "17:00",
    icon: Music,
    name: "The Opera House & Opera Park",
    body:
      "To round off the sightseeing, rent a bike or an e-scooter — or simply walk — over to see the Opera House and the brand-new Opera Park, the lush green garden that now wraps around it. A beautiful, breezy final stop before dinner, right on the water.",
    img: IMG.opera,
    imgAlt: "The Copenhagen Opera House on the harbour",
  },
  {
    time: "18:30",
    icon: Utensils,
    name: "Dinner — take your pick",
    body:
      "End the day with dinner — three very different tables, all close by. **Væksthuset**, right in Opera Parken, serves dinner inside the park's beautiful greenhouse. **Christianshavns Færgecafé** (Strandgade 50) is a snug, old-school locals' favourite back by the canal. Or go all out at **Kadeau** (Wildersgade 10B) — two Michelin stars and the flavours of Bornholm, one of the city's truly great meals (book well ahead).",
    img: IMG.dinnerD3,
    imgAlt: "A burrata dish served for dinner in Christianshavn, Copenhagen",
  },
];

const DAY2 = [
  {
    time: "10:00",
    icon: ShoppingBasket,
    name: "Breakfast at Torvehallerne",
    body:
      "Start day two like a local at Torvehallerne, the city's glass-covered food market (it opens at 10:00). Grab breakfast — a flaky pastry and a proper coffee, a bowl of porridge at **Grød** — then browse the stalls of cheese, fresh fish, chocolate and more.",
    img: IMG.torvehallerne,
    imgAlt: "Torvehallerne food market in Copenhagen",
  },
  {
    time: "11:15",
    icon: Telescope,
    name: "The Round Tower (Rundetårn)",
    body:
      "A few minutes' walk away, climb Rundetårn — the 17th-century Round Tower built by Christian IV as an astronomical observatory. There are no stairs, just a wide spiralling cobbled ramp (legend says Tsar Peter the Great rode up it on horseback) that winds gently to a rooftop platform over the old town.",
    img: IMG.rundetaarn,
    imgAlt: "The spiral ramp inside Copenhagen's Round Tower",
  },
  {
    time: "12:15",
    icon: Coffee,
    name: "Gråbrødretorv",
    body:
      "Wander down to Gråbrødretorv, a cobbled, café-lined square and one of the oldest and prettiest corners of the old town. It's the perfect spot for photos or a beer under the trees when the sun is out.",
    img: IMG.graabroedretorv,
    imgAlt: "The café-lined cobbled square of Gråbrødretorv",
  },
  {
    time: "13:00",
    icon: Croissant,
    name: "The Latin Quarter & a cardamom bun",
    body:
      "Lose yourself in the Latin Quarter — colourful buildings, second-hand shops and cafés. Stop at **Skt. Peders Bageri** (Sankt Peders Stræde 29), Copenhagen's oldest bakery, for a cardamom bun, and eat your lunch from **Bear Slice** (Larsbjørnsstræde 25). After lunch, have a bottle of beer at **Floss Bar** (Larsbjørnsstræde 10).",
    img: IMG.latinquarter,
    imgAlt: "Cobbled streets of Copenhagen's Latin Quarter",
  },
  {
    time: "14:15",
    icon: ShoppingBag,
    name: "Shop & stroll Strøget",
    body:
      "Drift onto **Strøget**, one of Europe's longest pedestrian shopping streets, and follow it west toward the city hall square. Flagship stores and Danish design — **Illums Bolighus**, **Royal Copenhagen** — mixed with side-street finds. Tivoli waits just across Rådhuspladsen at the far end.",
    img: IMG.stroget,
    imgAlt: "Shoppers strolling the Strøget pedestrian street in Copenhagen",
  },
];

// Day 2 always ends at Tivoli — the gardens, then dinner.
const TIVOLI_FINALE = [
  {
    time: "15:00",
    icon: Mark,
    name: "Tivoli Gardens",
    ticket: TIVOLI_TICKET_URL,
    body:
      "Enter **Tivoli Gardens**, the 1843 pleasure gardens that inspired Walt Disney. Flower beds, the century-old wooden rollercoaster, and thousands of lights flickering on at dusk. And if you're not into the rides, the beautiful gardens alone are worth it — come just for the walk. One warning: on Fridays during peak season the evening concerts draw massive crowds — not worth it.",
    img: IMG.tivoli,
    imgAlt: "Tivoli Gardens illuminated at dusk, Copenhagen",
  },
  {
    time: "18:30",
    icon: Utensils,
    name: "Dinner in Tivoli — take your pick",
    body:
      "Stay for dinner inside Tivoli: grab a quick burger at **Gasoline Grill**, right inside the gardens. For a proper experience inside the garden, book **Figaro** bar & bistro — or go traditional Danish at **Restaurant Grøften** under the trees. Try **Feed** bistro just outside Tivoli if you want a proper meat meal. Easy alternative: graze your way through **Tivoli Food Hall** — if you don't mind crowds and a bit of noise.",
    img: IMG.dinnerD2,
    imgAlt: "A pan-seared fish dish served for dinner in Copenhagen",
  },
];

const PRACTICAL = [
  { icon: Bike, title: "Getting around", body: "Walk the centre, then rent a bike or hop the Metro — it's fast, clean and runs 24/7.", link: "/transport", linkLabel: "Transport guide" },
  { icon: MapPin, title: "Where to stay", body: "Base yourself in Indre By, Vesterbro or Nyhavn to keep both days walkable.", link: "/stay", linkLabel: "Where to stay" },
  { icon: Clock, title: "When to go", body: "May–September for long days and terraces; December for the Christmas markets.", link: "/copenhagen-in", linkLabel: "Month-by-month guide" },
];

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Copenhagen Itinerary", item: `${SITE}/copenhagen-itinerary` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "TravelGuide",
  name: "Copenhagen Itinerary — 3 Perfect Days in Copenhagen",
  url: `${SITE}/copenhagen-itinerary`,
  description:
    "How to spend 3 days in Copenhagen - The best Copenhagen itinerary — Amalienborg's changing of the guard, the Marble Church, Kastellet & the Little Mermaid, a Danish hotdog, Nyhavn, a canal cruise & Broens Gadekøkken, then Torvehallerne, the Round Tower & the Latin Quarter, ending with an evening at Tivoli and dinner inside the gardens, plus a third day.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const ITINERARY_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "3 perfect days in Copenhagen",
  itemListElement: [
    ...[...DAY1, ...DAY2, ...TIVOLI_FINALE, ...DAY3].map((s) => ({ name: s.name })),
  ].map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: s.name,
  })),
};

function StopCard({ stop, index, day }) {
  const reverse = index % 2 === 1;
  const testId = `stop-d${day}-${stop.time.replace(":", "")}`;
  return (
    <Reveal y={40}>
      <div
        data-testid={testId}
        className="grid md:grid-cols-2 gap-6 md:gap-12 items-center py-8 md:py-12 border-b border-zinc-200/70 last:border-0"
      >
        <div className={`order-2 ${reverse ? "md:order-2" : "md:order-1"}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 bg-[#DF3226] text-white font-display font-900 text-sm px-3 py-1 rounded-full">
              <Clock size={14} /> {stop.time}
            </span>
            <span className="w-9 h-9 rounded-xl bg-black text-white flex items-center justify-center shrink-0">
              <stop.icon size={18} />
            </span>
          </div>
          <h3 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
            {stop.ticket ? (
              <a
                href={stop.ticket}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`twodays-stop-ticket-${stop.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="underline decoration-[#DF3226]/50 underline-offset-8 hover: transition-colors"
              >
                {stop.name}
              </a>
            ) : (
              stop.name
            )}
          </h3>
          <p className="font-body text-base md:text-lg text-black/70 mt-4 leading-relaxed">
            {mdBold(stop.body)}
          </p>

          {stop.link && (
            <Link
              to={stop.link}
              data-testid={`${testId}-link`}
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] mt-5 hover:gap-2.5 transition-all"
            >
              {stop.linkLabel}
            </Link>
          )}
          {stop.extLink && (
            <a
              href={stop.extLink.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testId}-book`}
              className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] mt-5 hover:gap-2.5 transition-all"
            >
              {stop.extLink.label}
            </a>
          )}
        </div>

        <div className={`order-1 ${reverse ? "md:order-1" : "md:order-2"}`}>
          <div className="border border-zinc-200 shadow-brut overflow-hidden aspect-[4/3]">
            <img
              src={stop.img}
              alt={stop.imgAlt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700"
            />
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function ItineraryPage() {
  return (
    <main data-testid="two-days-page" className="pt-16 md:pt-20">
      <Seo
        title="3 Days in Copenhagen: The Perfect Local Itinerary"
        description="The perfect 3 days in Copenhagen, planned by a local — Little Mermaid, Nyhavn, a canal tour, Torvehallerne, the Latin Quarter and a Tivoli evening."
        path="/copenhagen-itinerary"
        image={IMG.nyhavn}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
        <script type="application/ld+json">{JSON.stringify(ITINERARY_SCHEMA)}</script>
      </Helmet>

      {/* hero */}
      <section className="relative bg-[#13141A] text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Nyhavn harbour, Copenhagen" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-28">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="twodays-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton title="Copenhagen Itinerary — 3 Perfect Days" testid="twodays-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">PERFECT 3 DAYS</p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            3 days<br />
            <span className="font-editorial italic font-600">Copenhagen itinerary</span>
          </motion.h1>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6 leading-relaxed">
            Copenhagen is small, walkable and endlessly photogenic, so
            three days is enough to fall for the city — and just about enough to cover the basics.
            <br />
            <br />
            Here&apos;s how we would spend 3 days in Copenhagen as first time visitors: the crown jewels on
            day one, the old town on day two — ending in Tivoli — and the soul of the city on day three.
            Swap day 2 for day 3 if you like, or mix as you please using our{" "}
            <Link
              to="/planner"
              data-testid="hero-planner-link"
              className="font-800 text-[#DF3226] underline underline-offset-4 hover:text-white transition-colors"
            >
              planner
            </Link>
            .
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button
              type="button"
              data-testid="jump-day-2"
              onClick={() => document.getElementById("day-2")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              Jump to day 2 <ArrowDown size={15} />
            </button>
            <button
              type="button"
              data-testid="jump-day-3"
              onClick={() => document.getElementById("day-3")?.scrollIntoView({ behavior: "smooth" })}
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black/40 text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors backdrop-blur-sm"
            >
              Jump to day 3 <ArrowDown size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* DAY 1 */}
      <section id="day-1" className="bg-white scroll-mt-16 md:scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-6 md:pb-8">
          <Reveal>
            <p className="inline-flex items-center bg-black text-white font-display font-900 text-sm md:text-base tracking-[0.2em] px-4 py-1.5 rounded-full mb-4">DAY ONE</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              The classics —<br />
              <span className="font-editorial italic font-600">royals, the harbour & Nyhavn</span>
            </h2>
          </Reveal>

          <div className="mt-6">
            {DAY1.map((stop, i) => (
              <Fragment key={stop.time}>
                <StopCard stop={stop} index={i} day={1} />
                {stop.name === "Nyhavn" && (
                  /* canal cruise booking block — sits at 15:15, between Nyhavn and Broens */
                  <Reveal y={40}>
                    <div
                      data-testid="twodays-canal-tour"
                      className="my-6 bg-[#13141A] text-white rounded-3xl shadow-brut overflow-hidden"
                    >
                      <div className="p-7 md:p-10">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="inline-flex items-center gap-1.5 bg-[#DF3226] text-white font-display font-900 text-sm px-3 py-1 rounded-full">
                            <Clock size={14} /> 15:15
                          </span>
                          <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                            <Ship size={18} />
                          </span>
                        </div>
                        <h3 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
                          <a
                            href="https://gyg.me/d4it0SF2"
                            target="_blank"
                            rel="noopener noreferrer sponsored"
                            data-testid="twodays-canal-tour-link"
                            className="underline decoration-[#DF3226]/60 underline-offset-8 hover: transition-colors"
                          >
                            The canal cruise
                          </a>
                        </h3>
                        <p className="font-body text-base md:text-lg text-white/80 mt-4 leading-relaxed max-w-2xl">
                          The single best way to understand Copenhagen. A one-hour cruise glides you past the Opera
                          House, the Black Diamond library, under impossibly low bridges and out to see the Little
                          Mermaid from the water. Boats leave straight from Nyhavn.
                        </p>
                      </div>
                    </div>
                  </Reveal>
                )}
              </Fragment>
            ))}
          </div>

          <Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <DayRoute day={1} />
              <Link
                to="/planner"
                data-testid="planner-suggestion-day-1"
                className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] hover:gap-2.5 transition-all"
              >
                Want to mix it up? Build your own itinerary
              </Link>
            </div>
            <p className="font-body text-xs text-black/50 mt-3">
              The route opens in Google Maps — save it to your phone and walk the day as written.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DAY 2 */}
      <section id="day-2" className="bg-[#F9F8F6] border-t border-dotted border-zinc-300 scroll-mt-16 md:scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-6 md:pb-8">
          <Reveal>
            <p className="inline-flex items-center bg-black text-white font-display font-900 text-sm md:text-base tracking-[0.2em] px-4 py-1.5 rounded-full mb-4">DAY TWO</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              The old town —<br />
              <span className="font-editorial italic font-600">markets, towers &amp; a Tivoli evening</span>
            </h2>
          </Reveal>

          <div className="mt-6">
            {DAY2.map((stop, i) => (
              <StopCard key={stop.time} stop={stop} index={i} day={2} />
            ))}
          </div>

          {/* Tivoli finale + dinner — the last activities of day 2 */}
          <div className="mt-6">
            {TIVOLI_FINALE.map((stop, i) => (
              <StopCard key={stop.time} stop={stop} index={4 + i} day={2} />
            ))}
          </div>

          <Reveal>
            <div className="mt-6 max-w-md">
              <GygTivoliCard testid="itinerary-tivoli-ticket" />
            </div>
          </Reveal>

          <Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <DayRoute day={2} />
              <Link
                to="/planner"
                data-testid="planner-suggestion-day-2"
                className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] hover:gap-2.5 transition-all"
              >
                Want to mix it up? Build your own itinerary
              </Link>
            </div>
            <p className="font-body text-xs text-black/50 mt-3">
              The route opens in Google Maps — save it to your phone and walk the day as written.
            </p>
          </Reveal>
        </div>
      </section>

      {/* DAY 3 */}
      <section id="day-3" className="bg-white border-t border-dotted border-zinc-300 scroll-mt-16 md:scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16 pb-6 md:pb-8">
          <Reveal>
            <p className="inline-flex items-center bg-black text-white font-display font-900 text-sm md:text-base tracking-[0.2em] px-4 py-1.5 rounded-full mb-4">DAY THREE</p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              The soul —<br />
              <span className="font-editorial italic font-600">canals, Christiania &amp; the harbour</span>
            </h2>
          </Reveal>

          <div className="mt-6">
            {DAY3.map((stop, i) => (
              <StopCard key={stop.time} stop={stop} index={i} day={3} />
            ))}
          </div>

          <Reveal>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <DayRoute day={3} />
            </div>
            <p className="font-body text-xs text-black/50 mt-3">
              The route opens in Google Maps — save it to your phone and walk the day as written.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA — straight after day 3 */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Want to tweak the plan?<br />
            <span className="font-editorial italic font-600">It loads straight into the planner</span>
          </h2>
          <Link
            to={ITINERARY_TRIP_URL}
            data-testid="twodays-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open in the planner
          </Link>
        </div>
      </section>

      {/* practical */}
      <section className="bg-[#F9F8F6] border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-3xl md:text-4xl mb-10">
              Before you go —{" "}
              <span className="font-editorial italic font-600">the essentials</span>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PRACTICAL.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06} y={30}>
                <div
                  data-testid={`practical-${i}`}
                  className="bg-white border border-zinc-200 shadow-brut p-6 h-full flex flex-col"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-black text-white shrink-0 mb-4">
                    <p.icon size={20} />
                  </div>
                  <h3 className="font-display font-900 tracking-tight text-xl">{p.title}</h3>
                  <p className="font-body text-sm text-black/70 mt-2 leading-relaxed flex-1">{p.body}</p>
                  <Link
                    to={p.link}
                    data-testid={`practical-link-${i}`}
                    className="inline-flex items-center gap-1.5 font-body font-800 text-sm text-[#DF3226] mt-4 hover:gap-2.5 transition-all"
                  >
                    {p.linkLabel}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* bookable tour — hygge & happiness culture walk, small groups */}
      <GygTourBand tourId="398347" label="BOOK A TOUR" />
      <ExploreLinks
        testid="explore-itinerary"
        links={[
          { label: "Been before? Copenhagen like a local — 3 days", to: "/copenhagen-like-a-local" },
          { label: "Visiting in winter? The cosy cold-weather plan", to: "/copenhagen-in-winter" },
          { label: "The prettiest detours — the photo-worthy streets", to: "/photo-worthy-streets" },
          { label: "Build your own version — the free planner", to: "/planner" },
        ]}
      />
      <ShareBar title="Copenhagen Itinerary — 3 Perfect Days" />
    </main>
  );
}
